import { useCallback } from "react";
import { POST } from "api";
import {
  ParagraphInterface,
  UserInputInterface,
  ChatResponseInterface,
} from "entities/interfaces";
import { userMsgId, gptMsgId, nextMsgId } from "utils/idHelpers";

interface UseChatLoaderProps {
  token: string;
  current: { chapter: string; section: string };
  setChapter: (chapter: {
    id: string;
    title: string;
    num: number;
    isBookmarked: boolean;
    isLastSection: boolean;
    thumbnail: string;
  }) => void;
  setIsLoading: (loading: boolean) => void;
  setNodesData: (
    data: (ParagraphInterface | UserInputInterface | ChatResponseInterface)[]
  ) => void;
  parseContent: (
    node: ParagraphInterface
  ) => ParagraphInterface | UserInputInterface | ChatResponseInterface;
  fetchNodeProgress: (nodeId: number) => Promise<string | null>;
  markParagraph: (paragraphId: string) => Promise<void>;
  loadChatProgress: (
    nodeId: number,
    courseIdCheck: string,
    sessionId: string
  ) => Promise<{ user_response: string; gpt_response: string } | null>;
  activeCourseIdRef: React.MutableRefObject<string | null>;
  sessionIdRef: React.MutableRefObject<string>;
}


const getChapterImage = async (chapterId: string): Promise<string> => {
  try {
    const res = await fetch("https://api.diadibi.net/getChapterImage", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ chapterId }),
    });
    const data = await res.json();
    return data?.result?.image || "";
  } catch (err) {
    console.error("Fehler beim Laden des Kapitelbildes:", err);
    return "";
  }
};


function orderByOutputs(
  nodes: (ParagraphInterface | UserInputInterface | ChatResponseInterface)[]
) {
  const map          = new Map(nodes.map(n => [String(n.id), n]));
  const indexInHist  = new Map(nodes.map((n, i) => [String(n.id), i]));
  const ordered: typeof nodes = [];
  const visited = new Set<string>();

  const visit = (node: any | undefined) => {
    if (!node || visited.has(String(node.id))) return;
    visited.add(String(node.id));
    ordered.push(node);

    const outs = (node.outputs ?? "")
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean)
      .map((s: string)  => s.split("@")[0])                     // "8024@0" → "8024"
      .filter((id: string)  => map.has(id))                    // nur Knoten, die wir wirklich geladen haben
      .sort((a: string, b: string) =>
        (indexInHist.get(a) ?? Infinity) -
        (indexInHist.get(b) ?? Infinity)
      );

    outs.forEach((id: string) => visit(map.get(id)));
  };


  const start =
        nodes.find(n => n.type === "start") ??
        nodes.find(n => n.type !== "response" && n.type !== "userInput") ??
        nodes[0];

  visit(start);

  nodes.forEach(n => {
    if (!visited.has(String(n.id))) ordered.push(n);
  });

  return ordered;
}


export const useChatLoader = ({
  token,
  current,
  setChapter,
  setIsLoading,
  setNodesData,
  parseContent,
  fetchNodeProgress,
  markParagraph,
  loadChatProgress,
  activeCourseIdRef,
  sessionIdRef,
}: UseChatLoaderProps) => {
  const getSectionHistoryParagraphs = async (sectionId: string): Promise<ParagraphInterface[]> => {
    const body = new URLSearchParams();
    body.append("userToken", token || "");
    body.append("option", sectionId);

    const history = await POST("getSectionHistory", body).then((res) => res.content || []);

    const paragraphs = await Promise.all(
      history.map(async (id: any) => {
        const paraRes = await POST("getParagraph", new URLSearchParams({ option: id }));
        return paraRes.result;
      })
    );


    // Fortschritt laden
    await Promise.all(
      paragraphs.map(async (paragraph) => {
        const isInteractive = [
          "Multiple Choice",
          "Multiple-Choice",
          "Drag-and-Drop",
          "Drag-and-Drop-Multiple",
          "Sort-the-Paragraph",
          "Sort"
        ].includes(paragraph.title);

        if (isInteractive) {
          const progress = await fetchNodeProgress(paragraph.id);
          if (progress) {
            const parsed = JSON.parse(progress);
            paragraph.progressState = parsed.progressState;
            paragraph.selectedOptions = parsed.selectedOptions ?? [];
            paragraph.placedElements = parsed.placedElements ?? {};
            paragraph.points = parsed.points ?? 0;
            paragraph.requiresInput = true;
          }
        }
      })
    );

    return paragraphs;
  };

  const getChatData = useCallback(
    async (courseId: string, sessionId: string) => {
      activeCourseIdRef.current = courseId;
      if (sessionIdRef.current !== sessionId) return;

      setIsLoading(true);

      // 1. Kapitel & Sektionen holen
      const chaptersBody = new URLSearchParams();
      chaptersBody.append("userToken", token || "");
      chaptersBody.append("option", courseId);

      const { result: chapters } = await POST("getChapters", chaptersBody);
      if (!chapters?.length) {
        console.error("❌ Keine Kapitel gefunden");
        setIsLoading(false);
        return;
      }
      

      // 2. Kapitel-Fortschritt prüfen
      const chapterProgress = await Promise.all(
        chapters.map(async (chapter: any) => {
          const body = new URLSearchParams();
          body.append("userToken", token || "");
          body.append("option", chapter.id.toString());
          body.append("content", "2");
          const { result } = await POST("getUserProgress", body);
          return parseInt(result);
        })
      );

      // 3. Erstes offenes Kapitel + Sektion finden
      let found = false;

      for (let i = 0; i < chapters.length && !found; i++) {
        if (chapterProgress[i] === 0 || chapterProgress[i] === 1) {
          current.chapter = chapters[i].id;
          const sectionResp = await POST("getSections", new URLSearchParams({ option: current.chapter }));
          const sections = sectionResp.result || [];
          const image = await getChapterImage(current.chapter);
          chapters[i].thumbnail = image; 
          const sectionProgress = await Promise.all(
            sections.map(async (section: any) => {
              const body = new URLSearchParams();
              body.append("userToken", token || "");
              body.append("option", section.id);
              body.append("content", "3");
              const { result } = await POST("getUserProgress", body);
              return parseInt(result);
            })
          );
          

          for (let j = 0; j < sections.length; j++) {
            if (sectionProgress[j] === 0 || sectionProgress[j] === 1) {
              current.section = sections[j].id;
              
              setChapter({
                id: chapters[i].id,
                title: chapters[i].title,
                num: chapters[i].ordering,
                isBookmarked: chapters[i].bookmark === "1",
                isLastSection: i === chapters.length - 1 && j === sections.length - 1,
                thumbnail: chapters[i].thumbnail || "",
              });

              found = true;
              break;
            }
          }
        }
      }

      if (!found) {
        current.chapter = chapters[0].id;
        const fallback = await POST("getSections", new URLSearchParams({ option: current.chapter }));
        current.section = fallback.result?.[0]?.id || "";

        setChapter({
          id: chapters[0].id,
          title: chapters[0].title,
          num: chapters[0].ordering,
          isBookmarked: chapters[0].bookmark === "1",
          isLastSection: true,
          thumbnail: chapters[0].thumbnail || " ",
        });
      }

      // 4. Paragraphen + Fortschritt laden
      let paragraphsData = await getSectionHistoryParagraphs(current.section);

      if (!paragraphsData.length) {
        const fallback = await POST("getParagraphs", new URLSearchParams({ option: current.section }));
        const startNode = fallback.result?.find((p: ParagraphInterface) => p.type === "start");
        if (startNode) {
          paragraphsData.push(startNode);
          await markParagraph(startNode.id);
        }
      }

      if (!paragraphsData.length) {
        setNodesData([]);
        setIsLoading(false);
        return;
      }

      const parsedData = paragraphsData.map(p => ({
        ...parseContent(p),
        chatStatus: p.type === 'chat' ? 'waiting' : 'done',
      }));
      
      const completeNodes: (ParagraphInterface | UserInputInterface | ChatResponseInterface)[] = [];

      for (const raw of parsedData) {
        // --------------- CHAT -----------------------------
        if (raw.type === "chat") {
          const chat = await loadChatProgress(raw.id, courseId, sessionId);
      
          if (chat) {
            // Chat war bereits beantwortet: nur die "fertige" Variante einfügen
            const doneChat: ParagraphInterface = {
              ...raw,
              requiresInput: false,
            };
            completeNodes.push(doneChat);
      
            // User- und GPT-Nachricht
            const userMsg: UserInputInterface = {
              id         : userMsgId(raw.id),
              content    : chat.user_response,
              type       : "userInput",
              section_id : raw.section_id ?? 0,
              isLeft     : false,
              outputs    : "",
              prompt     : "",
              prompt_type: "",
              settings   : "",
              status     : "",
              title      : "",
            };
      
            const gptMsg: ChatResponseInterface = {
              id         : gptMsgId(raw.id),
              content    : chat.gpt_response,
              type       : "response",
              section_id : raw.section_id ?? 0,
              isLeft     : true,
              outputs    : "",
              prompt     : "",
              prompt_type: "",
              settings   : "",
              status     : "",
              title      : "",
            };
      
            completeNodes.push(userMsg, gptMsg);



            // synthetischer "Weiter"-Knoten
            completeNodes.push({
              id         : nextMsgId(raw.id),
              type       : "next",
              outputs    : raw.outputs || "",
              requiresInput: false,
              section_id : raw.section_id ?? 0,
              content    : "",
              prompt     : "",
              prompt_type: "",
              settings   : "",
              status     : "",
              title      : "",
            });

            doneChat.outputs = String(userMsg.id);
            userMsg.outputs  = String(gptMsg.id);
            gptMsg.outputs = String(nextMsgId(raw.id));
            continue;           
          }
        }

        completeNodes.push(raw);
      }
      
      const uniqueById = new Map<number, any>();
      completeNodes.forEach(n => uniqueById.set(n.id, n));
      const ordered = orderByOutputs([...uniqueById.values()]);
      
      setNodesData(ordered);
      setIsLoading(false);
    },
    [token, sessionIdRef]
  );

  return { getChatData };
};
